/** Rearranging and removing a task's figures, shown before the server agrees. */

import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  FigureLayoutRequest,
  FigureLayoutResponse,
  FigureListResponse,
  FigurePagePreferenceModel,
} from "@patent-studio/api-types";
import type { Lanes } from "@/components/ui/SortableLanes";
import { placedFileIds, projectFigureLabels } from "@/lib/figure-labels";
import { ApiError, refusalDetail } from "@/services/api-client";
import {
  listFigures,
  replaceFigureLayout,
  restoreFigure,
  setAsideFigure,
} from "@/services/figures";
import { figuresKey, stageSourcesScope } from "./query-keys";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/**
 * The layout's entries with the optionality resolved.
 *
 * The generated type makes both `entries` and the response's `layout` optional,
 * because each has a server-side default. Absent and empty mean the same thing
 * here, so they are collapsed once on the way in rather than branched on at
 * every use.
 */
type Entries = NonNullable<FigureLayoutRequest["entries"]>;

/**
 * The lane a top-level position belongs to.
 *
 * Sorting across containers is expressed as one array per lane, and a lane is
 * addressed by name. The top level needs a name of its own for that, and a
 * group's lane needs one that cannot collide with the group's own row: the row
 * sits inside the top-level array while the lane is a key beside it, and the
 * drag helper looks a drop target up as a key only after failing to find it as
 * a row. Sharing one identifier would make a drop meant for the inside of a
 * group land beside it instead, and the write would persist that.
 */
export const ROOT = "root";

/** The lane holding one group's members. */
export function laneOf(groupId: string): string {
  return `lane:${groupId}`;
}

/** Every lane the screen drew, each holding its contents in order. */
export type { Lanes };

/** One change to the arrangement, as asked for rather than as computed. */
type LayoutChange =
  | { kind: "arrange"; lanes: Lanes }
  | { kind: "new-group"; at: number; groupId: string; lanes: Lanes }
  | { kind: "retitle-group"; groupId: string; title: string }
  | { kind: "dissolve-group"; groupId: string }
  | { kind: "remove"; fileId: string }
  | { kind: "restore"; fileId: string }
  | {
      kind: "page-preference";
      fileId: string;
      preference: FigurePagePreferenceModel;
    };

type Entry = Entries[number];
type Group = Extract<Entry, { kind: "group" }>;

function isGroup(entry: Entry): entry is Group {
  return entry.kind === "group";
}

/** A figure's placement. The same shape whether it sits in a group or beside one. */
type Placement = NonNullable<Group["members"]>[number];

function placement(fileId: string): Placement {
  return { kind: "figure" as const, file_id: fileId };
}

/**
 * The layout these lanes describe.
 *
 * The top-level lane is authoritative for order and holds groups alongside
 * figures, so a name in it is a group when the layout already knows that name
 * and a figure otherwise. A group's members come from its own lane.
 *
 * An omission is treated as silence rather than as instruction, because the
 * write replaces the layout whole and an arrangement is built from what one
 * screen drew. A group whose lane is unmentioned keeps the members it has, a
 * group missing from the top level is kept, and so is a figure the lanes never
 * name: the screen draws every figure it knows about, so an unnamed one is one
 * that arrived after the drag began, and unplacing it would undo the position
 * its upload just gave it. Dissolving a group is its own operation, asked for
 * rather than inferred.
 */
function arranged(entries: Entries, lanes: Lanes, known: Set<string>): Entries {
  const groups = new Map(
    entries.filter(isGroup).map((group) => [group.group_id, group]),
  );
  const filled = (group: Group): Entry => {
    const key = laneOf(group.group_id);
    return {
      ...group,
      // A group holds figures and nothing else, so a name inside one that is
      // not a figure the task holds is dropped rather than written out as a
      // figure. The drag rules already refuse a group aimed at a lane; this is
      // the same rule where the arrangement is turned into the layout, so the
      // depth cannot be exceeded by a lane that arrived any other way.
      members: Object.hasOwn(lanes, key)
        ? lanes[key].filter((id) => known.has(id)).map(placement)
        : (group.members ?? []),
    };
  };

  const top = lanes[ROOT] ?? [];
  const kept = entries
    .filter(isGroup)
    .filter((group) => !top.includes(group.group_id));

  const mentioned = new Set(Object.values(lanes).flat());
  const stranded = entries.filter(
    (entry) => !isGroup(entry) && !mentioned.has(entry.file_id),
  );

  const placed = [
    // A name that is neither a group the layout knows nor a figure the task
    // holds is dropped rather than written out as a figure. It reaches here
    // when a lane outlives what it named, and naming a file the task has no
    // figure for makes the server refuse the whole arrangement, so the stale
    // name would cost the attorney every position rather than its own.
    ...top.flatMap((id) => {
      const group = groups.get(id);
      if (group !== undefined) return [filled(group)];
      return known.has(id) ? [placement(id)] : [];
    }),
    ...kept.map(filled),
    ...stranded,
  ];

  // A name placed twice would be written twice, which the server refuses whole.
  // Refusing here instead costs the attorney one position rather than the
  // entire arrangement they just made. Groups are held to this as well as
  // figures: the server's rule is that no identifier repeats, of either kind.
  const seen = new Set<string>();
  const once = (id: string) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  };
  return placed
    .map((entry) =>
      isGroup(entry)
        ? {
            ...entry,
            members: (entry.members ?? []).filter((m) => once(m.file_id)),
          }
        : entry,
    )
    .filter((entry) =>
      isGroup(entry) ? once(entry.group_id) : once(entry.file_id),
    );
}

/** The layout with a new empty group at one top-level position. */
function withNewGroup(entries: Entries, at: number, groupId: string): Entries {
  const group: Entry = {
    kind: "group" as const,
    group_id: groupId,
    title: "",
    members: [],
  };
  return [...entries.slice(0, at), group, ...entries.slice(at)];
}

/** The layout with one group renamed, holding everything else still. */
function retitled(entries: Entries, groupId: string, title: string): Entries {
  return entries.map((entry) =>
    isGroup(entry) && entry.group_id === groupId ? { ...entry, title } : entry,
  );
}

/**
 * The layout with one group's members returned to the top level, in its place.
 *
 * Removing a group removes the grouping, not the drawings. The members take the
 * position the group held and are renumbered as standalone figures, which is
 * undone by making the group again. Deleting them would not be.
 */
function dissolved(entries: Entries, groupId: string): Entries {
  return entries.flatMap((entry): Entries =>
    isGroup(entry) && entry.group_id === groupId
      ? (entry.members ?? []).map((member) => placement(member.file_id))
      : [entry],
  );
}

/** The layout with one figure gone, wherever it sits. */
function without(entries: Entries, fileId: string): Entries {
  return entries
    .filter((entry) => entry.kind === "group" || entry.file_id !== fileId)
    .map((entry) =>
      entry.kind === "group"
        ? {
            ...entry,
            members: (entry.members ?? []).filter(
              (member) => member.file_id !== fileId,
            ),
          }
        : entry,
    );
}

/**
 * The served view this layout produces, without asking the server for it.
 *
 * The order is the layout's own, and the labels come from the projection the
 * server runs against the same tree, which is what lets a figure carry its new
 * number the instant it is dropped rather than a round trip later.
 */
function viewOf(
  served: FigureListResponse,
  entries: Entries,
  preferences: Record<string, FigurePagePreferenceModel>,
  removed?: string,
): FigureListResponse {
  const labels = projectFigureLabels({ entries });
  const placed = placedFileIds(entries);
  const placedSet = new Set(placed);

  const known = served.figures ?? [];
  const byId = new Map(known.map((f) => [f.file_id, f]));
  const figures = placed
    .map((fileId) => byId.get(fileId))
    .filter((f) => f !== undefined)
    .map((f) => ({ ...f, label: labels[f.file_id] ?? null }));
  // An unplaced figure still belongs to the task, so it follows rather than
  // disappearing from a screen that is the only place it can be placed. A
  // deleted one is a different case: it is leaving the task, not its position.
  const unplaced = known.filter(
    (f) => !placedSet.has(f.file_id) && f.file_id !== removed,
  );

  // The version rides through unchanged. The arrangement shown is one the
  // server has not answered for yet, so what it was built on is still the
  // version the figures were read at, and that is what the write has to name.
  // Spread the served response rather than rebuilding it. Arranging speaks only for the
  // figures and their layout, and every other field here belongs to a screen this one knows
  // nothing about. Naming the fields instead would drop them for as long as the write is in
  // flight, which is invisible on this screen and wrong on the next.
  return {
    ...served,
    figures: [...figures, ...unplaced],
    layout: {
      entries,
      page_preferences: preferences,
      version: served.layout?.version ?? 0,
    },
  };
}

/**
 * The preferences that may ride a write of these entries.
 *
 * The server refuses a preference naming an unplaced figure, so a removal has
 * to take the figure's preference with it or poison every later layout write.
 */
function placedPreferences(
  preferences: Record<string, FigurePagePreferenceModel>,
  entries: Entries,
): Record<string, FigurePagePreferenceModel> {
  const placed = new Set(placedFileIds(entries));
  return Object.fromEntries(
    Object.entries(preferences).filter(([fileId]) => placed.has(fileId)),
  );
}

/**
 * Take the version the store gave a write it applied, leaving the screen alone.
 *
 * A change the attorney made before that answer arrived was built on the
 * arrangement this write just stored, so it has to name the version this write
 * was given rather than the one the figures were read at, which the store has
 * already moved past. Only the version is taken: the entries on screen are
 * further ahead than the ones just written, and showing that is the point.
 */
function noteApplied(
  qc: QueryClient,
  key: QueryKey,
  applied: FigureLayoutResponse,
): void {
  const showing = qc.getQueryData<FigureListResponse>(key);
  if (showing === undefined) return;
  qc.setQueryData(key, {
    ...showing,
    layout: {
      entries: showing.layout?.entries ?? [],
      page_preferences: showing.layout?.page_preferences ?? {},
      version: applied.version,
    },
  });
}

/** Whether two arrangements place the same figures in the same order. */
function sameArrangement(a: Entries, b: Entries): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The arrangement the change asks for, against what is on screen now. */
function entriesFor(change: LayoutChange, served: FigureListResponse): Entries {
  const entries = served.layout?.entries ?? [];
  const known = new Set((served.figures ?? []).map((f) => f.file_id));
  switch (change.kind) {
    case "arrange":
      return arranged(entries, change.lanes, known);
    case "new-group":
      // Placed against the arrangement on screen rather than the one the
      // server holds. A figure with no position yet is a row the attorney
      // can see and count from, while being absent from the layout the
      // group is spliced into, so the two disagree about where "before this
      // one" is. Settling the screen's order first makes them agree.
      return withNewGroup(
        arranged(entries, change.lanes, known),
        change.at,
        change.groupId,
      );
    case "retitle-group":
      return retitled(entries, change.groupId, change.title);
    case "dissolve-group":
      return dissolved(entries, change.groupId);
    case "remove":
      return without(entries, change.fileId);
    case "page-preference":
    // Where a restored figure goes is the store's to say; the answer is read back.
    case "restore":
      return entries;
  }
}

/** The preferences the change asks for, against what is on screen now. */
function preferencesFor(
  change: LayoutChange,
  served: FigureListResponse,
  entries: Entries,
): Record<string, FigurePagePreferenceModel> {
  const base = served.layout?.page_preferences ?? {};
  const asked =
    change.kind === "page-preference"
      ? { ...base, [change.fileId]: change.preference }
      : base;
  return placedPreferences(asked, entries);
}

/**
 * Sends the arrangement, and rebuilds it once if the layout moved underneath.
 *
 * Read when the turn comes rather than when the drag ended: queued behind an
 * earlier write, an arrangement captured at drag time predates whatever
 * happened while it waited, and sending it last would undo that.
 */
async function writeLayout(
  task: string,
  change: LayoutChange,
  qc: QueryClient,
  key: QueryKey,
): Promise<unknown> {
  // Read when the turn comes, not when the drag ended. Queued behind an
  // earlier write, an arrangement captured at drag time predates whatever
  // happened while it waited, and sending it last would undo that.
  const showing = qc.getQueryData<FigureListResponse>(key);
  try {
    const applied = await replaceFigureLayout(task, {
      entries: showing?.layout?.entries ?? [],
      page_preferences: showing?.layout?.page_preferences ?? {},
      version: showing?.layout?.version ?? 0,
    });
    noteApplied(qc, key, applied);
    return applied;
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    // The stored layout is no longer the one this arrangement was made
    // against, which an upload landing mid-drag is enough to cause. The
    // arrangement is still what the attorney asked for, so it is rebuilt
    // against the layout that is there now and sent once more.
    const fresh = await listFigures(task);
    const freshEntries = entriesFor(change, fresh);
    const applied = await replaceFigureLayout(task, {
      entries: freshEntries,
      page_preferences: preferencesFor(change, fresh, freshEntries),
      version: fresh.layout?.version ?? 0,
    });
    noteApplied(qc, key, applied);
    return applied;
  }
  // Once only. Two screens rearranging one task refuse each other, and a
  // retry per refusal is a loop rather than a reconciliation.
}

/**
 * Puts the arrangement back, and says so, when a write is refused.
 *
 * Only the arrangement still on screen may be taken off it: a refusal that has
 * been overtaken belongs to a state the attorney has already moved on from,
 * and restoring its snapshot would undo the change they made after it.
 */
function restoreAfterRefusal(
  change: LayoutChange,
  previous: FigureListResponse | undefined,
  qc: QueryClient,
  key: QueryKey,
  error?: unknown,
) {
  const showing = qc.getQueryData<FigureListResponse>(key);
  const stillShowing =
    showing !== undefined &&
    previous !== undefined &&
    sameArrangement(
      showing.layout?.entries ?? [],
      entriesFor(change, previous),
    );

  // Only the arrangement still on screen may be taken off it. A refusal
  // that has been overtaken belongs to a state the attorney has already
  // moved on from, and restoring its snapshot would undo the change they
  // made after it.
  if (stillShowing) {
    qc.setQueryData(key, previous);
  }

  if (change.kind === "restore") {
    // A refusal names what stopped it and what to do, in the store's own words.
    toast.error(
      error instanceof ApiError && error.status === 409
        ? refusalDetail(error, "Could not restore that figure.")
        : "Could not restore that figure. Please try again.",
    );
    return;
  }
  if (change.kind === "remove") {
    // Said whether or not it was overtaken. Nothing else deletes the file,
    // so silence here leaves the attorney believing a figure is gone that
    // the next refetch will bring back without explanation.
    toast.error("Could not delete that figure. Please try again.");
    return;
  }
  // A later arrangement replaces the layout whole, so it repairs this one.
  // Reporting a failure it has already undone is a false alarm.
  if (stillShowing) {
    toast.error("Could not save the new arrangement. Please try again.");
  }
}

/**
 * The named changes, each a mutate call the screen can make.
 *
 * A group's identifier is minted here rather than inside the write, so the
 * group shown and the group sent are the same one: minting it at send time
 * would give a retry a second identifier and leave the attorney with two
 * groups where they asked for one.
 */
function useLayoutActions(
  taskId: string | undefined,
  mutate: (change: LayoutChange) => void,
) {
  const arrange = useCallback(
    (lanes: Lanes) => {
      if (taskId === undefined) return;
      mutate({ kind: "arrange", lanes });
    },
    [mutate, taskId],
  );

  // The identifier is minted here rather than inside the write, so the group
  // shown and the group sent are the same one. Minting it at send time would
  // give a retry a second identifier and leave the attorney with two groups
  // where they asked for one.
  const newGroup = useCallback(
    (at: number, lanes: Lanes) => {
      if (taskId === undefined) return;
      mutate({ kind: "new-group", at, groupId: crypto.randomUUID(), lanes });
    },
    [mutate, taskId],
  );

  const dissolveGroup = useCallback(
    (groupId: string) => {
      if (taskId === undefined) return;
      mutate({ kind: "dissolve-group", groupId });
    },
    [mutate, taskId],
  );

  const setPagePreference = useCallback(
    (fileId: string, preference: FigurePagePreferenceModel) => {
      if (taskId === undefined) return;
      mutate({ kind: "page-preference", fileId, preference });
    },
    [mutate, taskId],
  );

  return {
    arrange,
    newGroup,
    dissolveGroup,
    setPagePreference,
  };
}

/**
 * Remove and its Undo, through the arrangement's queue, each answering whether it landed.
 *
 * Apart from the arrangement's other acts because the screen waits on these: the toast that
 * says where a removed figure went shows only once it is there.
 */
function useSetAsideActs(
  taskId: string | undefined,
  mutateAsync: (change: LayoutChange) => Promise<unknown>,
  qc: QueryClient,
  key: QueryKey,
) {
  // Setting the figure aside is what removes the placement; the server takes the entry out
  // as part of the same write. The layout is computed here anyway, because the row has to
  // leave and the ones after it renumber before the request is answered. Answers whether
  // it landed, so the toast that says where the figure went waits for it.
  const remove = useCallback(
    (fileId: string) => {
      const served = qc.getQueryData<FigureListResponse>(key);
      // A figure already on its way out is not removed twice. The second request would
      // answer that it is gone, which is true, and be reported as though it had failed.
      if (taskId === undefined || served === undefined)
        return Promise.resolve(false);
      if (!(served.figures ?? []).some((f) => f.file_id === fileId))
        return Promise.resolve(false);
      return mutateAsync({ kind: "remove", fileId }).then(
        () => true,
        () => false,
      );
    },
    [key, mutateAsync, qc, taskId],
  );

  const restore = useCallback(
    (fileId: string) =>
      taskId === undefined
        ? Promise.resolve(false)
        : mutateAsync({ kind: "restore", fileId }).then(
            () => true,
            () => false,
          ),
    [mutateAsync, taskId],
  );

  return { remove, restore };
}

/**
 * Change the arrangement: show it at once, write it, reconcile if refused.
 *
 * Rearranging and removing live in one hook because they change one thing. Each
 * write replaces the layout whole, so two in flight together are settled by
 * whichever the server applies last, and that is not necessarily the
 * arrangement the attorney left behind. One queue holds both: a queue per
 * operation looks like serialisation and is not, because the guarantee then
 * breaks exactly at the boundary between them, which is where tidying a list
 * actually happens.
 */
export function useFigureLayout(taskId: string | undefined) {
  const qc = useQueryClient();
  const key = figuresKey(taskId ?? "");
  const inFlightRef = useRef<Promise<unknown>>(Promise.resolve());
  const issuedRef = useRef(0);

  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.figureLayout,
    mutationFn: async (change: LayoutChange) => {
      const send = (): Promise<unknown> => {
        if (taskId === undefined) return Promise.resolve();
        // Set aside, not erased: the figure waits in Deleted Figures for Undo or Restore.
        if (change.kind === "remove")
          return setAsideFigure(taskId, change.fileId);
        if (change.kind === "restore")
          return restoreFigure(taskId, change.fileId);
        return writeLayout(taskId, change, qc, key);
      };
      const queued = inFlightRef.current.then(send, send);
      // Swallowed on the chain only: the next write must follow this one either
      // way, while this one's own failure still reaches its handler.
      inFlightRef.current = queued.catch(() => undefined);
      return queued;
    },

    onMutate: async (change: LayoutChange) => {
      // A refetch landing mid-drag would overwrite the arrangement with the one
      // the server still believes in.
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<FigureListResponse>(key);
      if (previous !== undefined) {
        const entries = entriesFor(change, previous);
        const view = viewOf(
          previous,
          entries,
          preferencesFor(change, previous, entries),
          change.kind === "remove" ? change.fileId : undefined,
        );
        qc.setQueryData(key, view);
      }
      issuedRef.current += 1;
      return { previous, ticket: issuedRef.current };
    },

    onError: (error, change, context) => {
      restoreAfterRefusal(change, context?.previous, qc, key, error);
    },

    onSettled: async (_data, _error, _change, context) => {
      // Only the last write of a flurry reconciles. Refetching between two
      // queued writes would pull in a layout the second is about to replace,
      // and show it in the gap.
      if (context?.ticket !== issuedRef.current) return;
      await qc.invalidateQueries({ queryKey: key });
      // The figure set is what Figures and Generate draw, so their sources move with it.
      if (taskId !== undefined) {
        await qc.invalidateQueries({ queryKey: stageSourcesScope(taskId) });
      }
    },
  });

  const { mutate } = mutation;

  const { arrange, newGroup, dissolveGroup, setPagePreference } =
    useLayoutActions(taskId, mutate);
  // Waited on by the title field, so a refused title stays in it to be sent again.
  const { mutateAsync } = mutation;
  const retitleGroup = useCallback(
    async (groupId: string, title: string) => {
      if (taskId === undefined) return;
      await mutateAsync({ kind: "retitle-group", groupId, title });
    },
    [mutateAsync, taskId],
  );

  // Behind whatever is queued, so an Undo pressed before its remove has landed
  // follows that remove rather than racing it. Answers whether it landed, so the
  // Undo says the figure is back only once it is.
  const { remove, restore } = useSetAsideActs(
    taskId,
    mutation.mutateAsync,
    qc,
    key,
  );

  return {
    ...mutation,
    arrange,
    setPagePreference,
    newGroup,
    retitleGroup,
    dissolveGroup,
    remove,
    restore,
  };
}
